import { h } from 'vue'
import { callTurnToConfigFn, encodeArray } from 'util/turnto-utils'
import { Comment as IComment } from 'services/api-service'
import Comment from 'components/comment/Comment.vue'
import { createAndMountApp } from 'util/loader-helper'
import msg from 'services/msg'
import topCommentsConfig from './top-comments.config'
import topCommentsService from 'services/api/top-comments'
import topCommentsStore from 'store/top-comments-store'
import visibleObserver from 'util/visible-observer'
import trackingService from 'services/tracking-service'

const topCommentClass = 'tt-top-comment'
const processedAttrName = 'data-ttprocessed'
const skuAttrName = 'data-ttsku'

/**
 * Gets the skus for all the elements placed in the page by the client.  They will all have the
 * same class name.  It will not return skus that have already been processed (populated with a
 * comment).
 */
function getUnprocessedSkus() {
  const topElems = document.getElementsByClassName(topCommentClass)
  const skus = []
  for (let i = 0; i < topElems.length; i++) {
    const elem = topElems.item(i)
    if (!elem.getAttribute(processedAttrName)) {
      const sku = elem.getAttribute(skuAttrName)
      if (sku) {
        skus.push(sku)
      }
    }
  }
  return skus
}

/**
 * Finds the div placed in the page by the client for the given sku and populates with the sku.
 *
 * @param sku
 * @param comment
 * @param skus
 */
function insertCommentForSku(sku: string, comment: IComment, skus: string[]) {
  // shouldn't happen, but nothing from stopping client from putting same sku in the page
  // multiple times
  const topElems = document.querySelectorAll(`[${skuAttrName}="${sku.replace(/"/g, '\\"')}"]`)
  for (let i = 0; i < topElems.length; i++) {
    const elem = topElems.item(i)
    // just make sure not already populated
    if (!elem.getAttribute(processedAttrName)) {
      visibleObserver.register(elem, () => {
        trackingService.queueEvent('top-comments', 'view', sku, {
          skus,
          tgs: topCommentsStore.tags
        })
        visibleObserver.unregister(elem)
      })

      // Vue replaces the element you provide for "el", and we want to keep the one the client
      // placed in the page, so creating a div inside that can be replaced.
      // const replaceableDivId = `tt-tcc-${i}`
      const replaceableDiv = document.createElement('div')
      // replaceableDiv.setAttribute('id', replaceableDivId)
      elem.appendChild(replaceableDiv)
      const props = {
        comment,
        truncateLength: topCommentsConfig.truncateLength,
        truncateAllowance: topCommentsConfig.truncateAllowance,
        metaType: 'ed',
        prefixText: msg('comment.prefix')
      }
      createAndMountApp({ // eslint-disable-line no-new
        render: () => h(Comment, props)
      }, null, replaceableDiv)
      elem.setAttribute(processedAttrName, 'true')
    }
  }
}

/**
 * This is the entry point for top comments. This will look for all divs provided by the client
 * to have top comments and populate them if there are comments for that item.
 * This can be called multiple times so that the client can add more to the page after the page
 * has already been loaded.
 */
function processTopComments(): void {
  const skus = getUnprocessedSkus()
  if (skus.length > 0) {
    topCommentsService.topComments({
      skus: encodeArray(skus),
      tags: encodeArray(topCommentsStore.tags),
      minCharCount: topCommentsConfig.minCharacterCount,
      minWordCount: topCommentsConfig.minWordCount,
      includeSyndicated: topCommentsConfig.includeSyndicated
    }).then((data) => {
      trackingService.queueEvent('top-comments', 'load', null, {
        skus,
        tags: topCommentsStore.tags
      })

      // The data object is a map of sku to array of comments. Only using one comment so just
      // passing the first one from the array
      Object.keys(data).forEach((sku) => {
        if (data[sku] && data[sku].length > 0) {
          insertCommentForSku(sku, data[sku][0], skus)
        }
      })
      // if client provided an onFinish function
      callTurnToConfigFn('topComments', 'onFinish')
    }).catch(() => {})
  }
}

export default processTopComments
